import { BookingSlot, UpdateSlotAction } from 'api-types/models/Booking';
import useUserSettings from '../../hooks/useUserSettings';
import { Modal } from 'components/modal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from 'api/errors';
import { useUpdateBookingGuestsMutation } from '../../api/bookingApi';

import './index.css';

interface IProps {
    slot: BookingSlot;
    updateSlot: (slot: BookingSlot, action: UpdateSlotAction) => void;
}

export function AddGuestToBooking({ slot, updateSlot }: Readonly<IProps>) {
    const { t } = useTranslation();

    const { data: userSettings } = useUserSettings();
    const [updateBookingGuests] = useUpdateBookingGuestsMutation();

    const [isAddGuestModalOpen, setIsAddGuestModalOpen] =
        useState<boolean>(false);
    const [guests, setGuests] = useState<string[]>([]);
    const [error, setError] = useState<string>();

    const slotGuests = userSettings
        ? (slot.guests[userSettings.email] ?? [])
        : [];

    const handleCloseModal = () => setIsAddGuestModalOpen(false);

    const handleAddOrRemoveGuest = (guest: string) => {
        setGuests(
            guests.includes(guest)
                ? guests.filter((g) => g !== guest)
                : guests.concat(guest)
        );
    };

    const handleUpdateSlotWithGuests = () => {
        updateBookingGuests({ slotId: slot.slotId, guests })
            .unwrap()
            .then((response) => {
                updateSlot(response, UpdateSlotAction.GUEST);
                setIsAddGuestModalOpen(false);
            })
            .catch((error: unknown) => {
                if (error instanceof ApiError) {
                    setError(error.message);
                }
            });
    };

    useEffect(() => {
        if (userSettings) {
            setGuests(slot.guests[userSettings.email] ?? []);
        }
    }, [userSettings, slot]);

    return (
        <>
            <button
                className='btn btn-gray'
                onClick={() => setIsAddGuestModalOpen(true)}
            >
                + {t('add_guest')}
            </button>
            {isAddGuestModalOpen && (
                <Modal
                    title='add_guest'
                    actionButtons={
                        <>
                            <button
                                className='btn btn-gray'
                                onClick={handleCloseModal}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                className='btn btn-blue'
                                disabled={!guests.length && !slotGuests.length}
                                onClick={handleUpdateSlotWithGuests}
                            >
                                {t('submit')}
                            </button>
                        </>
                    }
                    closeModal={handleCloseModal}
                    content={
                        <div className='add-guest-modal'>
                            <p>{t('add_guest_hint')}</p>
                            {!userSettings?.guests?.length && (
                                <p>{t('add_guest_no_profile_guests')}</p>
                            )}
                            <div className='guests'>
                                {userSettings?.guests?.map((guest) => (
                                    <div
                                        className={`guest-chip ${
                                            guests.includes(guest)
                                                ? 'active'
                                                : ''
                                        }`}
                                        key={guest}
                                        onClick={() =>
                                            handleAddOrRemoveGuest(guest)
                                        }
                                    >
                                        {guest}
                                    </div>
                                ))}
                            </div>
                            <div className='error'>
                                <small>{error}</small>
                            </div>
                        </div>
                    }
                ></Modal>
            )}
        </>
    );
}
